import type {
  AppConfigResponse,
  AccessRecoveryRequest,
  AccessRecoveryResponse,
  CheckoutRequest,
  CheckoutResponse,
  CheckoutStatusResponse,
  CreateReportRequest,
  ReportResponse,
  SearchResponse,
} from "../shared/types";

const readJson = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get("Content-Type") ?? "";

  if (!contentType.includes("application/json")) {
    throw new Error("A API não respondeu em JSON. Confirme se o Worker está ativo em localhost:8787.");
  }

  const payload = (await response.json()) as T | { error?: string; detail?: string };

  if (!response.ok) {
    const errorPayload = payload as { error?: string; detail?: string };
    throw new Error(errorPayload.detail || errorPayload.error || "Pedido falhou");
  }

  return payload as T;
};

export const getAppConfig = async () => {
  const response = await fetch("/api/config");
  return readJson<AppConfigResponse>(response);
};

export const searchStands = async (query: string) => {
  const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
  return readJson<SearchResponse>(response);
};

export const createReport = async (request: CreateReportRequest) => {
  const response = await fetch("/api/reports", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });
  return readJson<ReportResponse>(response);
};

export const getReport = async (reportId: string, accessToken?: string | null, qa?: string | null) => {
  const params = new URLSearchParams();

  if (accessToken) {
    params.set("accessToken", accessToken);
  }

  if (qa) {
    params.set("qa", qa);
  }

  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  const response = await fetch(`/api/reports/${reportId}${suffix}`);
  return readJson<ReportResponse>(response);
};

export const createCheckout = async (request: CheckoutRequest) => {
  const response = await fetch("/api/checkout", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });
  return readJson<CheckoutResponse>(response);
};

export const getCheckoutStatus = async (sessionId: string) => {
  const response = await fetch(`/api/checkout/status?sessionId=${encodeURIComponent(sessionId)}`);
  return readJson<CheckoutStatusResponse>(response);
};

export const recoverAccess = async (request: AccessRecoveryRequest) => {
  const response = await fetch("/api/access/recover", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });
  return readJson<AccessRecoveryResponse>(response);
};

export const downloadReportPdf = async (reportId: string, accessToken: string) => {
  const response = await fetch(`/api/reports/${reportId}/pdf?accessToken=${encodeURIComponent(accessToken)}`);

  if (!response.ok) {
    const payload = (await response.json()) as { error?: string };
    throw new Error(payload.error || "Não foi possível gerar o PDF");
  }

  return response.blob();
};
